import type { SecondaryOrderStatePath } from 'state/secondaryMarket/secondaryMarket.model';
import { toValidationErrors } from 'state/secondaryMarket/shared/amend.utils';
import { formOriginalSelectorsFactory } from 'state/secondaryMarket/shared/form/original/original.selector';
import type { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import type { AppState } from 'state/state.model';

export function orderSelectorsFactory<S>(statePath: Extract<SecondaryOrderStatePath, 'limitOrder'>, selectors: S) {
  const originalSelectors = formOriginalSelectorsFactory(statePath, {});
  const select = (state: AppState, tabId: string) => {
    return state.secondaryMarket[statePath].order[tabId];
  };

  const formatToPriceType = (state: AppState, tabId: string, price: number | undefined) => {
    if (price === undefined) {
      return price;
    }
    const priceType = originalSelectors.getPriceType(state, tabId) ?? getTradePriceType(state, tabId);
    return priceType === 'Percent' ? price * 100.0 : price;
  };

  const getTradeStatus = (state: AppState, tabId: string) => {
    return select(state, tabId)?.orderStatus;
  };

  const getTradePriceType = (state: AppState, tabId: string) => {
    return select(state, tabId)?.priceType;
  };

  const getTradeSalesMargin = (state: AppState, tabId: string) => {
    return select(state, tabId)?.salesMargin;
  };

  const getTradeSalesMarginFormatted = (state: AppState, tabId: string) => {
    const salesMargin = getTradeSalesMargin(state, tabId);
    return formatToPriceType(state, tabId, salesMargin);
  };

  const getTradeTradeWay = (state: AppState, tabId: string) => {
    return select(state, tabId)?.tradeWay;
  };
  const getCashAmount = (state: AppState, tabId: string) => {
    return select(state, tabId)?.cashAmount;
  };

  const getTradeSettlementDate = (state: AppState, tabId: string) => {
    return select(state, tabId)?.settlementDate;
  };

  const getTradeTradeDate = (state: AppState, tabId: string) => {
    return select(state, tabId)?.tradeDate;
  };

  const getTradeErrorMessage = (state: AppState, tabId: string) => {
    return select(state, tabId)?.errorMessage;
  };

  const getTradeTimeout = (state: AppState, tabId: string) => {
    return select(state, tabId)?.timeout;
  };

  const getTradeQuantity = (state: AppState, tabId: string) => {
    return select(state, tabId)?.quantity;
  };

  const getTradeValidationErrors = (state: AppState, tabId: string): SecondaryMarketFormError[] => {
    const validations = select(state, tabId)?.validations ?? [];
    return toValidationErrors(validations);
  };

  return {
    select,
    formatToPriceType,
    getTradeStatus,
    getTradePriceType,
    getTradeSalesMargin,
    getTradeSalesMarginFormatted,
    getTradeTradeWay,
    getTradeSettlementDate,
    getTradeTradeDate,
    getTradeErrorMessage,
    getTradeTimeout,
    getTradeQuantity,
    getTradeValidationErrors,
    getCashAmount,
    ...selectors,
  };
}
